import { Link, useNavigate } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { commitMutation, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import { environment } from "@app/relay/relay-environment";
import { clearAuthToken, getAuthToken } from "@app/relay/fetch-relay";
import { getDateRange, dateRangeFromISO } from "@app/lib/date-range";
import { deleteSession } from "@app/mutations/DeleteSession";
import CategoryExpensesChart from "./category-expenses-chart";
import GenerateExportButton from "@app/components/generate-export-button";
import KpiChart from "@app/components/kpi-chart";
import ExplainButton from "@app/components/explain/explain-button";
import type { ExplainMetric } from "@app/components/explain/explain-panel";
import MonthDetail from "./month-detail";
import type { dashboardPage_Query } from "@app/queries/dashboardPage_Query.graphql";
import type { DeleteSession_DeleteSessionMutation } from "@app/queries/DeleteSession_DeleteSessionMutation.graphql";

export const dashboardQuery = graphql`
  query dashboardPage_Query($perspectiveId: ID, $months: Int) {
    dataHealth {
      gapMonths
      accounts {
        number
        gapMonths
        staleSince
      }
    }
    expensesByCategory(perspectiveId: $perspectiveId) {
      category {
        id
        name
      }
      points {
        month
        value
      }
    }
    metrics(months: $months) {
      monthlyRevenue {
        month
        value
      }
      monthlyStripeRevenue {
        month
        value
      }
      monthlyExpenses {
        month
        value
      }
      monthlyNet {
        month
        value
      }
      cashPosition {
        month
        value
      }
      netCashPosition {
        month
        value
      }
      ttmRevenue {
        month
        value
      }
      ttmRevenueGrowth {
        month
        value
      }
      ttmProfitMargin {
        month
        value
      }
      ruleOf40 {
        month
        value
      }
    }
  }
`;

type Point = {
  readonly month: string;
  readonly value: number | null | undefined;
};

function lastValue(points: ReadonlyArray<Point>): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const value = points[i].value;
    if (value != null) return value;
  }
  return null;
}

function money(value: number | null): string {
  if (value == null) return "—";
  const abs = Math.abs(value);
  const formatted =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(2)}M`
      : abs >= 1_000
      ? `${(abs / 1_000).toFixed(0)}k`
      : abs.toFixed(0);
  return `${value < 0 ? "-" : ""}$${formatted}`;
}

function percent(value: number | null): string {
  return value == null ? "—" : `${(value * 100).toFixed(1)}%`;
}

// --- Rule of 40 decomposition + granularity --------------------------------
// Rule of 40 = TTM revenue growth % + TTM (cash-basis EBITDA) margin %. Both
// components are already computed by the backend; here we surface them as a
// stacked decomposition so the drivers of the number are visible.

type Granularity = "monthly" | "quarterly" | "yearly";

const GRANULARITIES: ReadonlyArray<Granularity> = [
  "monthly",
  "quarterly",
  "yearly",
];

// Every monthly point is already a trailing-twelve-month (annualized) reading,
// so quarterly/yearly views just thin the series to period-end months — the way
// a VC reads Rule of 40 at board cadence — without distorting the metric. The
// latest month is always kept: its TTM value is valid even mid-period.
function periodEndMonths(
  months: ReadonlyArray<string>,
  granularity: Granularity
): Set<string> {
  if (granularity === "monthly") return new Set(months);
  const isPeriodEnd = (month: string) => {
    const mm = month.slice(5, 7);
    return granularity === "yearly"
      ? mm === "12"
      : ["03", "06", "09", "12"].includes(mm);
  };
  const kept = months.filter(isPeriodEnd);
  const latest = months[months.length - 1];
  if (latest && !kept.includes(latest)) kept.push(latest);
  return new Set(kept);
}

function keepMonths(
  points: ReadonlyArray<Point>,
  keep: Set<string>
): Point[] {
  return points.filter((p) => keep.has(p.month));
}

// Rule of 40 is expressed in points; its growth/margin components are fractions
// (0.12 = 12%). Scale them to points so the stacked bars sum to the total.
function toPoints(points: ReadonlyArray<Point>): Point[] {
  return points.map((p) => ({
    month: p.month,
    value: p.value == null ? null : p.value * 100,
  }));
}

const quarterLabel = (month: string) =>
  `Q${Math.ceil(Number(month.slice(5, 7)) / 3)} '${month.slice(2, 4)}`;
const yearLabel = (month: string) => month.slice(0, 4);

// Downloads the board-pack workbook from the authenticated export endpoint.
// The JWT lives in localStorage (not a cookie), so a plain <a href> wouldn't
// carry it — we fetch() with the bearer header and trigger a blob download.
// Threads the global date-range lens through as the board pack's `from` bound.
// Queues the build instead of streaming it: the pack derives six tabs of metrics,
// which is not work for a request thread.
function BoardPackExportButton() {
  return (
    <GenerateExportButton
      kind="BOARD_PACK"
      label="Generate board pack"
      from={dateRangeFromISO(getDateRange())}
    />
  );
}

export default function DashboardPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<dashboardPage_Query>;
}) {
  const data = usePreloadedQuery(dashboardQuery, queryRef);
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [r40Granularity, setR40Granularity] = useState<Granularity>("monthly");

  const handleLogout = () => {
    setLoggingOut(true);
    commitMutation<DeleteSession_DeleteSessionMutation>(environment, {
      mutation: deleteSession,
      variables: {},
      // Drop the local token and hard-navigate to /login either way: the hard
      // load resets the Relay store, and being logged out locally is the safe
      // outcome even if the server call fails.
      onCompleted: () => {
        clearAuthToken();
        window.location.assign("/login");
      },
      onError: () => {
        clearAuthToken();
        window.location.assign("/login");
      },
    });
  };

  const metrics = data.metrics;
  const ruleOf40 = lastValue(metrics.ruleOf40);

  // Rule of 40 broken into its drivers: EBITDA margin (bottom) + YoY revenue
  // growth (top) stack to the total, with the total drawn as a line so it
  // reads correctly even when a component is negative. Resampled to the
  // selected granularity (monthly / quarterly / yearly).
  const r40Keep = periodEndMonths(
    metrics.ruleOf40.map((p) => p.month),
    r40Granularity
  );
  const ruleOf40Series = [
    {
      name: "EBITDA margin",
      points: keepMonths(toPoints(metrics.ttmProfitMargin), r40Keep),
      type: "bar" as const,
      color: "#0d9488",
      stack: "r40",
    },
    {
      name: "Revenue growth (YoY)",
      points: keepMonths(toPoints(metrics.ttmRevenueGrowth), r40Keep),
      type: "bar" as const,
      color: "#2563eb",
      stack: "r40",
    },
    {
      name: "Rule of 40",
      points: keepMonths(metrics.ruleOf40, r40Keep),
      type: "line" as const,
      color: "#7c3aed",
    },
  ];
  const r40FormatXLabel =
    r40Granularity === "quarterly"
      ? quarterLabel
      : r40Granularity === "yearly"
      ? yearLabel
      : undefined;

  const gapMonths = data.dataHealth.gapMonths;
  const gappedAccounts = data.dataHealth.accounts.filter(
    (a) => a.gapMonths.length > 0
  );
  const staleAccounts = data.dataHealth.accounts.filter(
    (a) => a.staleSince != null
  );

  const cards: {
    label: string;
    value: string;
    hint: string;
    explain?: ExplainMetric;
  }[] = [
    {
      label: "Rule of 40",
      value: ruleOf40 == null ? "—" : ruleOf40.toFixed(0),
      hint: "TTM growth % + TTM margin %",
      explain: "RULE_OF_40",
    },
    {
      label: "TTM revenue",
      value: money(lastValue(metrics.ttmRevenue)),
      hint: "trailing 12 months",
      explain: "TTM_REVENUE",
    },
    {
      label: "Revenue growth",
      value: percent(lastValue(metrics.ttmRevenueGrowth)),
      hint: "TTM vs prior TTM",
      explain: "REVENUE_GROWTH",
    },
    {
      label: "Profit margin",
      value: percent(lastValue(metrics.ttmProfitMargin)),
      hint: "TTM, cash basis",
      explain: "PROFIT_MARGIN",
    },
    {
      label: "Available cash",
      value: money(lastValue(metrics.cashPosition)),
      hint: "all cash accounts",
      explain: "NET_CASH",
    },
    {
      label: "Monthly net",
      value: money(lastValue(metrics.monthlyNet)),
      hint: "last month in - out",
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-end gap-3">
        <BoardPackExportButton />
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="rounded border px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
        >
          {loggingOut ? "Logging out…" : "Log out"}
        </button>
      </div>

      {(gappedAccounts.length > 0 || staleAccounts.length > 0) && (
        <Link
          to="/data-health"
          className="block rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100"
        >
          <span className="font-semibold">
            ⚠ Statement coverage issues — KPIs may be wrong.
          </span>{" "}
          {gappedAccounts.length > 0 &&
            `Missing months: ${gappedAccounts
              .map((a) => `${a.number} (${a.gapMonths.length})`)
              .join(", ")}. `}
          {staleAccounts.length > 0 &&
            `Stale: ${staleAccounts
              .map((a) => `${a.number} since ${a.staleSince}`)
              .join(", ")}. `}
          Shaded chart areas mark affected months — see the full report →
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded border p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-gray-500">{card.label}</p>
              {card.explain && <ExplainButton metric={card.explain} title={card.label} />}
            </div>
            <p className="text-2xl font-bold mt-1">{card.value}</p>
            <p className="text-[11px] text-gray-400 mt-1">{card.hint}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap rounded border bg-white px-4 py-2.5">
        <span className="text-sm font-medium text-gray-600">By year</span>
        <span className="text-xs text-gray-400">last 5 complete years —</span>
        <ExplainButton metric="ANNUAL_REVENUE_GROWTH" title="Revenue growth by year" label="Revenue growth (YoY)" />
        <ExplainButton metric="ANNUAL_ARR" title="ARR by year" label="ARR" />
        <ExplainButton metric="ANNUAL_EBITDA" title="EBITDA by year" label="EBITDA" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KpiChart
          title="Monthly revenue"
          series={[
            {
              name: "Revenue",
              points: metrics.monthlyRevenue,
              type: "bar",
              color: "#16a34a",
            },
            {
              name: "Expenses",
              points: metrics.monthlyExpenses,
              type: "bar",
              color: "#dc2626",
            },
            ...(metrics.monthlyStripeRevenue.length > 0
              ? [
                  {
                    name: "Stripe revenue",
                    points: metrics.monthlyStripeRevenue,
                    type: "line" as const,
                    color: "#6366f1",
                  },
                ]
              : []),
          ]}
          format="money"
          gapMonths={gapMonths}
          onMonthClick={setSelectedMonth}
        />
        <div className="relative">
          <div className="absolute right-3 top-2 z-10 inline-flex overflow-hidden rounded border bg-white text-[11px]">
            {GRANULARITIES.map((g, i) => (
              <button
                key={g}
                type="button"
                onClick={() => setR40Granularity(g)}
                className={`px-2 py-0.5 capitalize ${i > 0 ? "border-l" : ""} ${
                  r40Granularity === g
                    ? "bg-violet-600 text-white"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <KpiChart
            title="Rule of 40"
            series={ruleOf40Series}
            format="number"
            referenceLine={{ value: 40, label: "40" }}
            formatXLabel={r40FormatXLabel}
            onMonthClick={setSelectedMonth}
          />
        </div>
        <KpiChart
          title="TTM revenue growth"
          series={[
            {
              name: "Growth",
              points: metrics.ttmRevenueGrowth,
              type: "line",
              color: "#2563eb",
            },
            {
              name: "Margin",
              points: metrics.ttmProfitMargin,
              type: "line",
              color: "#d97706",
            },
          ]}
          format="percent"
          onMonthClick={setSelectedMonth}
        />
        {/* The same two lines /cash-position draws, from the same service and
            in the same colors — the dashboard is a summary of that page, so a
            reader moving between them must not have to reconcile two curves. */}
        <KpiChart
          title="Cash position"
          series={[
            {
              name: "Available cash",
              points: metrics.cashPosition,
              type: "line",
              color: "#111827",
            },
            {
              name: "Net cash position",
              points: metrics.netCashPosition,
              type: "line",
              color: "#0d9488",
            },
          ]}
          format="money"
          onMonthClick={setSelectedMonth}
        />
      </div>

      <CategoryExpensesChart
        buckets={data.expensesByCategory}
        axisMonths={metrics.monthlyExpenses.map((p) => p.month)}
        onMonthClick={(month) =>
          navigate({
            to: "/transactions/$month",
            params: { month },
            search: { flow: "COST" },
          })
        }
      />

      {selectedMonth && (
        <Suspense
          fallback={
            <div className="text-sm text-gray-500">
              Loading {selectedMonth}...
            </div>
          }
        >
          <MonthDetail
            month={selectedMonth}
            onClose={() => setSelectedMonth(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
